import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'

const API_URL = import.meta.env.VITE_API_URL || ''
const TOKEN_KEY = 'ourday_token'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY))
  const [loading, setLoading] = useState(true)

  // Persist token to localStorage whenever it changes
  const storeToken = useCallback((newToken) => {
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken)
    } else {
      localStorage.removeItem(TOKEN_KEY)
    }
    setToken(newToken)
  }, [])

  // Validate existing token on mount
  useEffect(() => {
    const validateToken = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY)
      if (!storedToken) {
        setLoading(false)
        return
      }

      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${storedToken}` },
        })

        if (res.ok) {
          const data = await res.json()
          setUser(data.user || data)
          setToken(storedToken)
        } else {
          // Token invalid or expired — clear it
          localStorage.removeItem(TOKEN_KEY)
          setToken(null)
          setUser(null)
        }
      } catch (error) {
        console.error('Token validation error:', error)
        localStorage.removeItem(TOKEN_KEY)
        setToken(null)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    validateToken()
  }, [])

  // Email + password login
  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Login failed' }))
      throw new Error(err.error || err.message || 'Login failed')
    }

    const data = await res.json()
    storeToken(data.token)
    setUser(data.user)
  }, [storeToken])

  // Legacy admin password-only login (no email)
  const loginLegacy = useCallback(async (password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Login failed' }))
      throw new Error(err.error || err.message || 'Invalid password')
    }

    const data = await res.json()
    storeToken(data.token)
    setUser(data.user || null)
  }, [storeToken])

  // Register new account
  const register = useCallback(async (email, password, name, phone) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, ...(phone ? { phone } : {}) }),
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Registration failed' }))
      throw new Error(err.error || err.message || 'Registration failed')
    }

    const data = await res.json()
    storeToken(data.token)
    setUser(data.user)
  }, [storeToken])

  // Logout — clear everything
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    setToken(null)
    setUser(null)
  }, [])

  // Update user profile
  const updateProfile = useCallback(async (data) => {
    if (!token) throw new Error('Not authenticated')

    const res = await fetch(`${API_URL}/api/auth/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Failed to update profile' }))
      throw new Error(err.error || err.message || 'Failed to update profile')
    }

    const result = await res.json()
    setUser(result.user || result)
    return result
  }, [token])

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    loginLegacy,
    register,
    logout,
    updateProfile,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
